import type { FetchResult, Operation } from '@apollo/client';

import type { GraphqlError } from './error-component';

import { ApolloLink } from '@apollo/client';
import i18next from 'i18next';

import { getErrorMessage } from './error-message-map';
import { InitializeEventProblemCode } from '../generated/graphql-manager';
import { CompleteAuthStepsProblemCode } from '../generated/graphql-viewer';
import { devLog } from '../utils';

const ERRORS_HANDLED_IN_UI = [
  'CreateAppearanceAsset',
  'CreateChat',
  'DeleteAppearanceAsset',
  'DeleteChat',
  'DeleteWebcastEvent',
  'DuplicateWebcast',
  'GetVideo',
  'ManageEventPermissions',
  'PollForViewers',
  'UpdateAccessCodePolicy',
  'UpdateChat',
  'UpdateEmailRegistrationPolicy',
  'UpdateEmailVerification',
  'UpdateEmailWhitelistPolicy',
  'UpdateEventPermission',
  'UpdateReferrerProtectionPolicy',
  'UpdateSingleSignOnPolicy',
  'UpdateTokenProtectionPolicy',
  'UpdateWebcast',
  'UpdateWebcastDescription',
  'UpdateWebcastEcdn',
  'UpdateWebcastLanguages',
  'UpdateWebcastProtocol',
  'UpdateWebcastRecordSettings',
];

const ERRORS_CODE_HANDLED_IN_UI = [
  CompleteAuthStepsProblemCode.INVALID_TOKEN,
  InitializeEventProblemCode.OPERATION_ALREADY_IN_PROGRESS,
];

const ERRORS_TO_IGNORE = [
  {
    operationName: 'PollForStreamInfo',
    message: 'HTTP fetch failed',
  },
];

export const problemsHandler = (setError: (error: GraphqlError) => void) => {
  return new ApolloLink((operation, forward) =>
    forward(operation).map((response) => {
      return handleResponse(response, operation, setError);
    })
  );
};

export const handleResponse = (
  response: FetchResult,
  operation: Operation,
  setError: (error: GraphqlError) => void
) => {
  const setErrorMessage = ({
    operationName,
    errorCode,
    errorMessage,
  }: {
    operationName: string;
    errorCode?: string;
    errorMessage?: string;
  }) => {
    const message = getErrorMessage(operationName);

    if (message) {
      setError(message);
    } else {
      setError({
        message: i18next.t('common.graphQlErrorDetails', {
          details: `${operationName} - ${errorCode || ''}${errorMessage || ''}`,
        }),
      });
    }
  };

  if (response.data) {
    // Don't do anything if we handle errors in UI
    if (ERRORS_HANDLED_IN_UI.includes(operation.operationName)) {
      for (const key of Object.keys(response.data)) {
        devLog('⚠️ ERRORS_HANDLED_IN_UI detected:', {
          operationName: operation.operationName,
          __typename: response.data[key]?.__typename,
          code: response.data[key]?.code,
        });
      }

      return response;
    }

    for (const key of Object.keys(response.data)) {
      const __typename = response.data[key]?.__typename;
      const code = response.data[key]?.code;

      if (ERRORS_CODE_HANDLED_IN_UI.includes(code)) {
        devLog('⚠️ ERRORS_CODE_HANDLED_IN_UI detected:', { errorCode: code });
        return response;
      }

      if (
        __typename?.endsWith('Error') ||
        __typename?.endsWith('Errors') ||
        __typename?.includes('Problem') ||
        __typename?.endsWith('Failure')
      ) {
        devLog('⚠️ GraphQL unhandled error detected:', { operationName: operation.operationName, __typename, code });
        setErrorMessage({ operationName: operation.operationName, errorCode: code });
      }
    }
  }

  // Errors sometimes come in `data` field, sometimes in `errors` field, so we need to handle both cases
  response.errors?.forEach(({ extensions, message }) => {
    devLog('⚠️ GraphQL Error:', { extensions, message });

    // Handled in UI
    if (
      (extensions?.errorType === 'NOT_FOUND' && message === 'Webcast not found') ||
      (extensions?.errorType === 'NOT_FOUND' && message.startsWith('VideoManager') && message.endsWith('not found')) ||
      (extensions?.errorType === 'BAD_REQUEST' && message === 'Invalid email address') ||
      (extensions?.errorType === 'BAD_REQUEST' && message === 'Email address not whitelisted')
    ) {
      devLog('⚠️ Error handled in UI detected:', {
        operationName: operation.operationName,
        errorType: extensions?.errorType,
        message,
      });
      return;
    }

    if (ERRORS_HANDLED_IN_UI.includes(operation.operationName)) {
      devLog('⚠️ ERRORS_HANDLED_IN_UI detected:', { operationName: operation.operationName });
      return response;
    }

    if (
      ERRORS_TO_IGNORE.some(
        (error) => error.operationName === operation.operationName && error.message.includes(message)
      )
    ) {
      devLog('⚠️ Ignored error detected:', { operationName: operation.operationName, message });
      return response;
    }

    devLog('⚠️ GraphQL unhandled error detected:', { operationName: operation.operationName, errorMessage: message });
    setErrorMessage({ operationName: operation.operationName, errorMessage: message });
  });

  return response;
};
